
import React from "react";
import { Calendar, MessageCircle, Rocket, Target, Phone, Workflow } from "lucide-react";

export const HowItWorks: React.FC = () => {
  return (
    <section id="how-it-works" className="py-20 md:py-32 bg-background overflow-hidden">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-20 fade-in">
          <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium bg-primary/10 text-primary rounded-full">
            The System
          </span>
          <h2 className="text-5xl md:text-6xl font-light text-foreground mb-6">
            We're experts at one thing: building with AI.
          </h2>
          <p className="text-2xl md:text-3xl text-muted-foreground">
            Every Hey Vail customer engagement starts with the same steps: map your business, connect your tools, and create AI workflows that work for you to move faster and scale.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 relative mb-16">
          {/* Connection line for desktop */}
          <div className="hidden md:block absolute top-24 left-[calc(16.67%+1rem)] right-[calc(16.67%+1rem)] h-0.5 bg-primary/20 z-0"></div>
          
          <div className="relative z-10 fade-in">
            <div className="glass-card p-8 border border-primary/10 h-full flex flex-col">
              <div className="feature-icon !bg-primary/10 mb-6">
                <Target className="h-5 w-5" />
              </div>
              <h3 className="text-3xl font-medium text-foreground mb-4">Map Your Business</h3>
              <p className="text-lg text-muted-foreground mb-4 flex-grow">
                Every business is different. What makes sense to automate for one company may need a human touch in another. Our job is to know the difference—so your customers feel cared for, not neglected.
              </p>
            </div>
          </div>
          
          <div className="relative z-10 fade-in" style={{ transitionDelay: "100ms" }}>
            <div className="glass-card p-8 border border-primary/10 h-full flex flex-col">
              <div className="feature-icon !bg-primary/10 mb-6">
                <Phone className="h-5 w-5" />
              </div>
              <h3 className="text-3xl font-medium text-foreground mb-4">Connect Your Tools</h3>
              <p className="text-lg text-muted-foreground mb-4 flex-grow">
                Connect Vail to your calendars, CRMs, and payment systems—it works with what you already use. Big companies pay consultants six figures for a single source of customer truth. With Vail, you get it built in.
              </p>
            </div>
          </div>
          
          <div className="relative z-10 fade-in" style={{ transitionDelay: "200ms" }}>
            <div className="glass-card p-8 border border-primary/10 h-full flex flex-col">
              <div className="feature-icon !bg-primary/10 mb-6">
                <Workflow className="h-5 w-5" />
              </div>
              <h3 className="text-3xl font-medium text-foreground mb-4">Create AI Workflows</h3>
              <p className="text-lg text-muted-foreground mb-4 flex-grow">
                AI can't replace every part of your business—and we won't pretend it can. What Vail does is handle the conversations AI is best at, saving you time, money, and frustration while giving your customers 24/7 communication.
              </p>
            </div>
          </div>
        </div>

        {/* Complete customer intelligence section */}
        <div className="mb-16">
          <h3 className="text-3xl font-light text-foreground mb-12 text-center">
            Complete customer intelligence in one system
          </h3>

          {/* 3-column layout with image and features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Column 1: Image/Graphic */}
            <div className="relative rounded-2xl overflow-hidden shadow-elevated aspect-square">
              <img
                src="https://images.unsplash.com/photo-1531297484001-80022131f5a1?auto=format&fit=crop&w=800&q=80"
                alt="Business dashboard"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-primary/0 to-background/80 flex items-end">
                <div className="p-6 text-foreground">
                  <div className="text-sm font-medium mb-1">Complete Revenue System</div>
                  <div className="text-xl font-light">AI that actually makes money</div>
                </div>
              </div>
            </div>

            {/* Columns 2-3: Features spread across remaining columns */}
            <div className="flex items-start gap-3">
              <div className="feature-icon !bg-primary/10 shrink-0 !w-10 !h-10">
                <Target className="h-4 w-4" />
              </div>
              <div>
                <h4 className="text-foreground font-medium mb-1">All Channels, One Record</h4>
                <p className="text-muted-foreground text-sm">SMS, email, phone, WhatsApp, chat—every interaction in one unified system</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="feature-icon !bg-primary/10 shrink-0 !w-10 !h-10">
                <Phone className="h-4 w-4" />
              </div>
              <div>
                <h4 className="text-foreground font-medium mb-1">24/7 Processing</h4>
                <p className="text-muted-foreground text-sm">Vail never sleeps, handles requests instantly, and escalates only when needed</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="feature-icon !bg-primary/10 shrink-0 !w-10 !h-10">
                <MessageCircle className="h-4 w-4" />
              </div>
              <div>
                <h4 className="text-foreground font-medium mb-1">Real-Time Tool Integration</h4>
                <p className="text-muted-foreground text-sm">Connect to your existing systems—calendars, CRMs, payment processors</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="feature-icon !bg-primary/10 shrink-0 !w-10 !h-10">
                <Workflow className="h-4 w-4" />
              </div>
              <div>
                <h4 className="text-foreground font-medium mb-1">Complete Automation</h4>
                <p className="text-muted-foreground text-sm">From first contact to deal closing—automate everything that doesn't need human touch</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="feature-icon !bg-primary/10 shrink-0 !w-10 !h-10">
                <Calendar className="h-4 w-4" />
              </div>
              <div>
                <h4 className="text-foreground font-medium mb-1">Full Context Always</h4>
                <p className="text-muted-foreground text-sm">AI and human teams always have complete conversation history across all channels</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="feature-icon !bg-primary/10 shrink-0 !w-10 !h-10">
                <Rocket className="h-4 w-4" />
              </div>
              <div>
                <h4 className="text-foreground font-medium mb-1">Scale Without Hiring</h4>
                <p className="text-muted-foreground text-sm">Handle 10x more customer interactions without adding staff</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
